import mixin from "@/utils/global-mixin";
import axios from "axios";
import store from "@/store";
import urlParse from "url-parse";
import distributionChannelUtil from "@/utils/distribution-channel-util";
import { MUTATION_NAME, STATUS_CODE } from "@/static/global-variables";

class DistributionChannelsService {
  constructor() {
    const studioDomain = urlParse(axios.defaults.baseURL).origin;
    this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL = `${studioDomain}/custom-display-elements/api/v1`;
  }

  distributionChannels() {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels`;
    return axios.get(url).then(({ data }) => {
      store.commit(MUTATION_NAME.DISTRIBUTION_CHANNELS_SUMMARY, data.data);
      return data.data;
    });
  }

  verifyWidget(formdata) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/uploads`;
    return axios
      .post(url, formdata)
      .then(({ data }) => {
        return data.data;
      })
      .catch(e => {
        let errorResponse = mixin.methods.getMessageText(
          store.state.messages.ERROR_WHILE_LOADING
        );
        if (e.response && e.response.status === STATUS_CODE.BAD_REQUEST) {
          errorResponse = distributionChannelUtil.localizeUploadErrorMessage(
            e.response.data.data,
            errorResponse
          );
        }
        console.error(e);
        return { errorResponse };
      });
  }

  createWidget(formdata, uuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels/${uuid}/widgets`;
    return axios.post(url, formdata).then(({ data }) => {
      const widget = data.data;
      if (!widget.name) {
        widget.name = formdata.get("name");
      }
      if (!widget.unpublished.version) {
        const version = `1.0.0-${formdata.get("preReleaseString")}`;
        widget.unpublished.version = version;
      }
      store.commit(MUTATION_NAME.ADD_NEW_WIDGET, widget);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return widget;
    });
  }

  distributionChannelLatestWidgets(uuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels/${uuid}/widgets/latest`;
    return axios.get(url).then(({ data }) => {
      const results = distributionChannelUtil.formatLatestWidgetsObject(
        uuid,
        data.data
      );
      store.commit(MUTATION_NAME.DISTRIBUTION_CHANNEL_LATEST_WIDGETS, results);
      return results;
    });
  }

  deleteUnpublishedWidget(versionUuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/versions/${versionUuid}`;
    return axios.delete(url).then(({ data }) => {
      store.commit(MUTATION_NAME.DELETE_LATEST_UNPUBLISHED_WIDGET, versionUuid);
      store.commit(
        MUTATION_NAME.DELETE_UNPUBLISHED_WIDGET_VERSION,
        versionUuid
      );
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return data.data;
    });
  }

  publishWidget(uuid) {
    const service = this;
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/versions/${uuid}`;
    return axios.put(url).then(({ data }) => {
      store.commit(MUTATION_NAME.SET_PUBLISHED_VERSION_LATEST_WIDGET, {
        ...data.data
      });
      const formatted = distributionChannelUtil.formatPublished([
        { ...data.data }
      ]);
      store.commit(
        MUTATION_NAME.SET_PUBLISHED_VERSION_WIDGET_VERSIONS,
        formatted[0]
      );
      service.distributionChannels().catch(console.error);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return data.data;
    });
  }

  distributionChannelDetails(uuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels/${uuid}`;
    return axios.get(url).then(({ data }) => {
      store.commit(MUTATION_NAME.DISTRIBUTION_CHANNEL_DETAIL, data.data);
      return data.data;
    });
  }

  distributionChannelName(uuid, name) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels/${uuid}`;
    return axios
      .put(url, name, { headers: { "content-type": "text/plain" } })
      .then(({ data }) => {
        store.commit(MUTATION_NAME.UPDATE_DISTRIBUTION_CHANNEL_DETAIL, {
          name
        });
        store.commit(MUTATION_NAME.UPDATE_DISTRIBUTION_CHANNEL_SUMMARY_NAME, {
          uuid,
          name
        });
        return data.data;
      });
  }

  widgetVersions(widgetuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/versions`;
    return axios.get(url).then(({ data }) => {
      const formatted = distributionChannelUtil.formatWidgetVersions(
        widgetuuid,
        data.data
      );
      store.commit(MUTATION_NAME.WIDGET_VERSIONS, formatted);
      return formatted;
    });
  }

  widgetVersionList(uuid, widgetuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/versions`;
    return axios.get(url).then(({ data }) => {
      const publishedVersions = distributionChannelUtil.formatWidgetPublishedVersions(
        uuid,
        widgetuuid,
        data.data
      );
      store.commit(
        MUTATION_NAME.CHANNEL_WIDGET_VERSION_LIST,
        publishedVersions
      );
      return publishedVersions;
    });
  }

  widgetTranslations(widgetuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/translations`;
    return axios.get(url).then(({ data }) => {
      const formatted = distributionChannelUtil.formatWidgetTranslations(
        widgetuuid,
        data.data
      );
      store.commit(MUTATION_NAME.WIDGET_TRANSLATIONS, formatted);
      return formatted;
    });
  }

  getWidgetTranslations(widgetId) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetId}/translations`;
    return axios.get(url).then(({ data }) => {
      store.commit(MUTATION_NAME.ALL_WIDGET_TRANSLATIONS, {
        uuid: widgetId,
        translations: data.data
      });
      return;
    });
  }

  createTranslation(translation, widgetuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/translations`;
    return axios.post(url, translation).then(({ data }) => {
      translation.uuid = data.data.uuid;
      store.commit(MUTATION_NAME.ADD_WIDGET_TRANSLATION, translation);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return translation;
    });
  }

  updateTranslation(translation) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/translations/${translation.uuid}`;
    return axios.put(url, translation).then(() => {
      store.commit(MUTATION_NAME.UPDATE_WIDGET_TRANSLATION, translation);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return translation;
    });
  }

  deleteWidgetTranslation(translation) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/translations/${translation.uuid}`;
    return axios.delete(url).then(({ data }) => {
      store.commit(MUTATION_NAME.DELETE_WIDGET_TRANSLATION, translation);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return data.data;
    });
  }

  createWidgetVersion(formdata, widgetuuid, widgetversionuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/versions`;
    function onSucceed(data) {
      const uuid = data.data.uuid;
      const version = `${formdata.get("version")}-${formdata.get(
        "preReleaseString"
      )}`;
      const newVersion = {
        widgetuuid,
        unpublished: { uuid, version }
      };
      store.commit(MUTATION_NAME.UPDATE_LATEST_UNPUBLISHED_VERSION, newVersion);
      store.commit(MUTATION_NAME.UPDATE_WIDGET_UNPUBLISHED_VERSION, newVersion);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return newVersion;
    }
    let promise;
    if (widgetversionuuid) {
      promise = axios
        .put(`${url}/${widgetversionuuid}`, formdata)
        .then(({ data }) => {
          return onSucceed(data);
        });
    } else {
      promise = axios.post(url, formdata).then(({ data }) => {
        return onSucceed(data);
      });
    }
    return promise;
  }

  widgetVersionDetails(widgetuuid, widgetversionuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/versions/${widgetversionuuid}`;
    return axios.get(url).then(({ data }) => {
      const resp = data.data;
      const formatted = distributionChannelUtil.formatWidgetVersionDetails(
        widgetversionuuid,
        resp
      );
      distributionChannelUtil.defineCustomComponent(resp.manifest, resp.jsFile);
      store.commit(MUTATION_NAME.WIDGET_VERSION_DETAILS, formatted);
      return formatted;
    });
  }

  getWidgetVersionDetails(widgetuuid, widgetversionuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/versions/${widgetversionuuid}`;
    return axios.get(url).then(({ data }) => {
      store.commit(MUTATION_NAME.ALL_WIDGET_VERSION_DETAILS, {
        uuid: widgetversionuuid,
        details: data.data
      });
      return;
    });
  }

  downloadWidgetVersion(widgetuuid, widgetversionuuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/widgets/${widgetuuid}/versions/${widgetversionuuid}/zip`;
    return axios.get(url, { responseType: "blob" }).then(({ data }) => {
      return data;
    });
  }

  customerDomains(uuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels/${uuid}/customers`;
    return axios.get(url).then(({ data }) => {
      const results = distributionChannelUtil.formatCustomerDomainList(
        uuid,
        data.data
      );
      store.commit(MUTATION_NAME.CUSTOMER_DOMAINS, results);
      return results;
    });
  }

  addCustomerDomain(customerDomain, uuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/distribution-channels/${uuid}/customers`;
    return axios.post(url, customerDomain).then(({ data }) => {
      const customer = {
        uuid: data.data.uuid,
        name: customerDomain.domain,
        fullAccess: customerDomain.fullAccess,
        widgetAccess: customerDomain.widgetAccess
      };
      store.commit(MUTATION_NAME.ADD_CUSTOMER_DOMAIN, customer);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return customer;
    });
  }

  updateCustomerDomainAccess(customer) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/customers/${customer.uuid}`;
    return axios.put(url, customer.payload).then(() => {
      store.commit(MUTATION_NAME.UPDATE_CUSTOMER_DOMAIN_LEVEL_ACCESS, customer);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return;
    });
  }

  deleteCustomerDomain(uuid) {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/customers/${uuid}`;
    return axios.delete(url).then(() => {
      store.commit(MUTATION_NAME.DELETE_CUSTOMER_DOMAIN, uuid);
      // reset variables that are used under Page to get any new changes
      store.commit(MUTATION_NAME.RESET_CHANNEL_PAGE_DATA);
      return;
    });
  }

  customerDomainList() {
    const url = `${this.CUSTOM_DISPLAY_ELEMENTS_BASE_URL}/domains/widgets`;
    return axios.get(url).then(({ data }) => {
      store.commit(MUTATION_NAME.DISTRIBUTION_CHANNEL_LIST, data.data);
      return;
    });
  }
}

export default new DistributionChannelsService();
